import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import {
  QBox,
  QButton,
  QTooltip,
  useCurrentUser,
  useDisclosure,
} from '@qualio/ui-components';

import { useExport } from '@qualio/export-components';
import { exportServiceClient } from '../../../../../../../api';
import { buildExportDocumentsPayload } from '../../../../../../../api/exportService';
import { BulkActionButtonProps } from '../BulkActionButton/index';
import { BulkExportModal } from './BulkExportModal';

type BulkExportButtonProps = Omit<
  BulkActionButtonProps,
  'isActionAllowed' | 'label' | 'onClick' | 'requiredPermissions'
>;

export const BulkExportButton = ({
  selectedDocuments,
}: BulkExportButtonProps) => {
  const { t } = useTranslation();
  const currentUser = useCurrentUser();

  const maxDocumentLimit = 500;
  const { trigger, isLoading } = useExport(exportServiceClient, {
    toastTitle: t('modals.bulkExportDocuments'),
    'data-cy': 'bulk-document-export-toast',
  });

  const {
    isOpen: isModalOpen,
    onOpen: openModal,
    onClose: closeModal,
  } = useDisclosure();

  const tooltip = useMemo(() => {
    if (isLoading) {
      return t('messages.aBulkExportAlreadyInProgress');
    }
    if (selectedDocuments.length < 1) {
      return t('messages.selectDocumentsToExport');
    }
    return t('messages.maximum500DocumentsBulkExported');
  }, [selectedDocuments.length, isLoading, t]);

  const handleExportDocuments = useCallback(
    (
      coverPageEnabled: boolean,
      controlledStatus: 'controlled' | 'uncontrolled',
      shouldShowShrinkingOption: boolean,
      contentShrinkingEnabled: boolean,
      includeChangeControl: boolean,
      includeDocx: boolean,
    ) => {
      trigger(
        buildExportDocumentsPayload(
          selectedDocuments,
          currentUser.companyId,
          true,
          {
            coverPageEnabled,
            controlledStatus,
            shouldShowShrinkingOption,
            contentShrinkingEnabled,
            includeChangeControl,
            includeDocx,
          },
        ),
      );
    },
    [trigger, selectedDocuments, currentUser.companyId],
  );

  const isDisabled = useMemo(() => {
    return (
      isLoading ||
      selectedDocuments.length < 1 ||
      selectedDocuments.length > maxDocumentLimit
    );
  }, [isLoading, selectedDocuments.length, maxDocumentLimit]);

  const handleBulkExport = useCallback(() => {
    openModal();
  }, [openModal]);

  if (selectedDocuments.length === 0) {
    return null;
  }

  return (
    <>
      {isDisabled && tooltip ? (
        <QTooltip label={tooltip}>
          <QBox>
            <QButton
              data-cy="bulk-export-button"
              variant="outline"
              isDisabled={isDisabled}
              onClick={handleBulkExport}
            >
              {t('buttons.bulkExport')}
            </QButton>
          </QBox>
        </QTooltip>
      ) : (
        <QButton
          data-cy="bulk-export-button"
          variant="outline"
          isDisabled={isDisabled}
          onClick={handleBulkExport}
        >
          {t('buttons.bulkExport')}
        </QButton>
      )}
      {isModalOpen && (
        <BulkExportModal
          isOpen={isModalOpen}
          onClose={closeModal}
          selectedDocuments={selectedDocuments}
          onExport={handleExportDocuments}
        />
      )}
    </>
  );
};
